import { useEffect, useRef, useState } from "react";
import { AtSign, CalendarClock, Check, CheckCheck, FileText, ImagePlus, MapPin, Mic, Plus, RefreshCcw, Reply, Search, Send, Smile, Square, UserCheck, UserPlus, UserX, Wifi, X, Zap } from "lucide-react";
import { Router } from "lucide-react";
import { renderQuickReply } from "../lib/quickReplies";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, humanTgl, rupiah } from "../lib/api";
import { AgentMutasiActions } from "./AgentMutasiActions";
import { playNotify } from "../lib/notifySound";
import { useAuth } from "../context/AuthContext";
import { PortalConfirmInline } from "./PortalConfirmInline";
import { compressImage } from "../lib/compressImage";
import { CekKodeDialog, guessCodes } from "./CekKodeDialog";
import { GantiVoucherDialog } from "./GantiVoucherDialog";
import { CekMasaBerlakuDialog } from "./CekMasaBerlakuDialog";
import { HotspotUsersDialog } from "./HotspotUsersDialog";
import { PppDataDialog } from "./PppDataDialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ZoomImg } from "./ZoomImg";
import { PppDiagDialog } from "./PppDiagDialog";

const API_BASE = api.defaults.baseURL;

const pppDiagOf = (m) => {
  if (m.ppp_diag?.router_id && m.ppp_diag?.secret_name) return m.ppp_diag;
  const mt = /^\/ppp\?router=([^&]+)&secret=(.+)$/.exec(m.link || "");
  return mt ? { router_id: mt[1], secret_name: decodeURIComponent(mt[2]) } : null;
};

const FIBI_STICKERS = [
  { id: "fibi-okeyyy", label: "Okeyyy !" },
  { id: "fibi-siap", label: "Siap Pak Bosss" },
  { id: "fibi-sabar", label: "Sabarrr Bosss" },
  { id: "fibi-proses", label: "Oce Prosesss..." },
  { id: "fibi-lelet", label: "Sinyal Lelet" },
  { id: "fibi-pulang", label: "Udah Pulang Kerja" },
];

export function ChatPanel({ channel, testid, compact = false, inputClass = "", templates = null, initialText = "" }) {
  const { user: me } = useAuth();
  const isGroup = channel === "admins:group";
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState(initialText);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [assignment, setAssignment] = useState(null);
  const [showHandlerHistory, setShowHandlerHistory] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);
  const isAdmin = ["admin", "admin_fasnet"].includes(me?.role);
  useEffect(() => {
    if (!isAdmin) return undefined;
    const load = () => api.get("/settings/quick-replies").then((r) => setQuickReplies(r.data.items || [])).catch(() => {});
    load();
    window.addEventListener("fasnet:quick-replies-changed", load);
    return () => window.removeEventListener("fasnet:quick-replies-changed", load);
  }, [isAdmin]);
  const mentionQuery = isAdmin ? (text.match(/(?:^|\s)@(\w*)$/) || [])[1] : undefined;
  const mentionOpts = mentionQuery !== undefined ? admins.filter((a) => a.id !== me?.id && a.name.toLowerCase().startsWith(mentionQuery.toLowerCase())).slice(0, 6) : [];
  const pickMention = (a) => setText(text.replace(/@(\w*)$/, `@${a.name.replace(/\s+/g, "")} `));
  const renderText = (t, mine) => {
    t = humanTgl(t);
    if (!t || !t.includes("@")) return t;
    return t.split(/(@\w+)/g).map((part, i) => (/^@\w+$/.test(part) ? <span key={i} className={`font-semibold rounded px-1 ${mine ? "bg-white/20" : "bg-amber-500/20 text-amber-400"}`}>{part}</span> : part));
  };
  const [delegateTo, setDelegateTo] = useState("");
  const [diag, setDiag] = useState(null);
  const assignedOther = !!assignment?.assigned && assignment.assigned_to !== me?.id && me?.role !== "admin";
  const superOverride = !!assignment?.assigned && assignment.assigned_to !== me?.id && me?.role === "admin";
  const bottomRef = useRef(null);
  const fileRef = useRef(null);
  const pdfRef = useRef(null);
  const prevCountRef = useRef(null);
  const typingSentRef = useRef(0);
  const [typingUsers, setTypingUsers] = useState([]);
  const [lastRead, setLastRead] = useState("");
  const [replyTarget, setReplyTarget] = useState(null);
  const [showStickers, setShowStickers] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [cekKode, setCekKode] = useState(false);
  const [hsUsers, setHsUsers] = useState(false);
  const [pppData, setPppData] = useState(false);
  const [cekInit, setCekInit] = useState("");
  const [ganti, setGanti] = useState(false);
  const [masaBerlaku, setMasaBerlaku] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const recRef = useRef(null);
  const recChunksRef = useRef([]);
  const recTimerRef = useRef(null);

  const stopRecording = (send) => {
    clearInterval(recTimerRef.current);
    const rec = recRef.current;
    if (rec && rec.state !== "inactive") {
      rec._send = send;
      rec.stop();
    }
    setRecording(false);
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("Browser tidak mendukung rekam suara");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg", "audio/mp4"].find((t) => MediaRecorder.isTypeSupported(t)) || "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime, audioBitsPerSecond: 32000 } : undefined);
      recChunksRef.current = [];
      rec._send = true;
      rec.ondataavailable = (e) => { if (e.data.size > 0) recChunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (!rec._send) return;
        const blob = new Blob(recChunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 1000) return;
        setUploading(true);
        try {
          const fd = new FormData();
          const ext = (rec.mimeType || "").includes("ogg") ? "ogg" : (rec.mimeType || "").includes("mp4") ? "m4a" : "webm";
          fd.append("file", new File([blob], `suara.${ext}`, { type: rec.mimeType || "audio/webm" }));
          const { data } = await api.post(`/chat/${encodeURIComponent(channel)}/voice`, fd);
          setMessages((m) => [...m, data]);
          toast.success("Pesan suara terkirim — otomatis terhapus dalam 2 jam");
        } catch (err) { toast.error(errMsg(err)); } finally { setUploading(false); }
      };
      recRef.current = rec;
      rec.start(250);
      setRecording(true);
      setRecSecs(0);
      recTimerRef.current = setInterval(() => {
        setRecSecs((s) => {
          if (s + 1 >= 60) { stopRecording(true); return 60; }
          return s + 1;
        });
      }, 1000);
    } catch {
      toast.error("Izin mikrofon ditolak — aktifkan izin mic di browser");
    }
  };

  useEffect(() => () => { clearInterval(recTimerRef.current); if (recRef.current && recRef.current.state !== "inactive") { recRef.current._send = false; recRef.current.stop(); } }, []);

  useEffect(() => { setReplyTarget(null); }, [channel]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/chat/${encodeURIComponent(channel)}/messages`);
        if (!alive) return;
        api.post("/chat/read", { channel }).catch(() => {});
        if (prevCountRef.current !== null && data.length > prevCountRef.current && data[data.length - 1]?.user_id !== me?.id) {
          playNotify();
        }
        prevCountRef.current = data.length;
        setMessages(data);
        const { data: pres } = await api.get(`/chat/${encodeURIComponent(channel)}/presence`);
        if (alive) { setTypingUsers(pres.typing || []); setLastRead(pres.last_read || ""); }
        if (isAdmin && !isGroup) {
          const { data: asg } = await api.get(`/chat/${encodeURIComponent(channel)}/assignment`);
          if (alive) setAssignment(asg);
        }
      } catch { /* retry di interval berikutnya */ }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [channel]);

  useEffect(() => {
    if (isAdmin) api.get("/chat/admins").then((r) => setAdmins(r.data)).catch(() => {});
  }, [isAdmin]);

  const claim = async () => {
    try {
      const { data } = await api.post(`/chat/${encodeURIComponent(channel)}/claim`);
      setAssignment(data);
      toast.success(`Chat ini sekarang Anda tangani (${data.assigned_name})`);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const release = async () => {
    try {
      await api.post(`/chat/${encodeURIComponent(channel)}/release`);
      setAssignment({ assigned: false });
      toast.success("Tugas dilepas — admin lain bisa mengambilnya");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const delegate = async () => {
    if (!delegateTo) return;
    try {
      const { data } = await api.post(`/chat/${encodeURIComponent(channel)}/delegate`, { to_user_id: delegateTo });
      setAssignment(data);
      setDelegateTo("");
      toast.success(`Tugas didelegasikan ke ${data.assigned_name}`);
    } catch (err) { toast.error(errMsg(err)); }
  };

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setSending(true);
    setText("");
    const replyId = replyTarget?.id || "";
    setReplyTarget(null);
    try {
      const { data } = await api.post(`/chat/${encodeURIComponent(channel)}/messages`, { text: t, reply_to: replyId });
      setMessages((m) => [...m, data]);
    } catch (err) { setText(t); if (err?.response?.status === 409) toast.error(errMsg(err)); } finally { setSending(false); }
  };

  const sendLocation = () => {
    if (!navigator.geolocation) { toast.error("Browser tidak mendukung lokasi"); return; }
    setUploading(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { data } = await api.post(`/chat/${encodeURIComponent(channel)}/messages`, { text: text.trim(), lat: pos.coords.latitude, lng: pos.coords.longitude });
        setMessages((m) => [...m, data]); setText("");
      } catch (err) { toast.error(errMsg(err)); } finally { setUploading(false); }
    }, (err) => { setUploading(false); toast.error(err.code === 1 ? "Izin lokasi ditolak — aktifkan izin lokasi di browser" : "Gagal mengambil lokasi, coba lagi di tempat terbuka"); }, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
  };

  const sendSticker = async (id) => {
    setShowStickers(false);
    try {
      const { data } = await api.post(`/chat/${encodeURIComponent(channel)}/messages`, { text: "", sticker: id });
      setMessages((m) => [...m, data]);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const sendImageFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const blob = await compressImage(file);
      const fd = new FormData();
      fd.append("file", new File([blob], "foto.jpg", { type: "image/jpeg" }));
      const { data } = await api.post(`/chat/${encodeURIComponent(channel)}/image`, fd);
      setMessages((m) => [...m, data]);
      toast.success("Gambar terkirim");
    } catch (err) { toast.error(errMsg(err)); } finally { setUploading(false); }
  };

  const sendImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    sendImageFile(file);
  };

  const sendPdf = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File PDF maksimal 5 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/chat/${encodeURIComponent(channel)}/pdf`, fd);
      setMessages((m) => [...m, data]);
      toast.success("PDF terkirim — otomatis terhapus dalam 30 menit");
    } catch (err) { toast.error(errMsg(err)); } finally { setUploading(false); }
  };

  const onPaste = (e) => {
    const items = e.clipboardData?.items || [];
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        e.preventDefault();
        sendImageFile(item.getAsFile());
        return;
      }
    }
  };

  const retract = async (id) => {
    try {
      await api.delete(`/chat/${encodeURIComponent(channel)}/messages/${id}`);
      setMessages((m) => m.filter((x) => x.id !== id));
      toast.success("Pesan ditarik");
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className={`flex flex-col gap-3${compact ? " h-full min-h-0" : ""}`} data-testid={testid}>
      {isAdmin && cekKode && <CekKodeDialog open onClose={() => { setCekKode(false); setCekInit(""); }} channel={channel} initialCode={cekInit || guessCodes(messages)[0] || ""} suggestions={guessCodes(messages)} />}
      {isAdmin && hsUsers && <HotspotUsersDialog open onClose={() => setHsUsers(false)} suggestions={guessCodes(messages)} onCekKode={(c) => { setCekInit(c); setHsUsers(false); setCekKode(true); }} />}
      {isAdmin && pppData && <PppDataDialog open onClose={() => setPppData(false)} suggestions={guessCodes(messages)} onDiag={(rid, n) => { setPppData(false); setDiag({ router_id: rid, secret_name: n }); }} />}
      {isAdmin && ganti && <GantiVoucherDialog open onClose={() => setGanti(false)} channel={channel} suggestions={guessCodes(messages)} />}
      {me?.role === "admin" && masaBerlaku && <CekMasaBerlakuDialog open onClose={() => setMasaBerlaku(false)} suggestions={guessCodes(messages)} />}
      {isAdmin && !isGroup && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-accent/40 px-3 py-2" data-testid={`${testid}-assign-bar`}>
          {channel.startsWith("agent:") && (
            <>
              <Button size="sm" variant="outline" className="border-[#F04E23]/40 text-[#F04E23] hover:bg-[#F04E23]/10" onClick={() => setCekKode(true)} data-testid={`${testid}-cek-kode-btn`}><Search className="w-4 h-4 mr-1" /> Cek Kode</Button>
              {me?.role === "admin" && <Button size="sm" variant="outline" className="border-emerald-500/40 text-emerald-500 hover:bg-emerald-500/10" onClick={() => setGanti(true)} data-testid={`${testid}-ganti-voucher-btn`}><RefreshCcw className="w-4 h-4 mr-1" /> Ganti Voucher</Button>}
              {me?.role === "admin" && <Button size="sm" variant="outline" className="border-violet-500/40 text-violet-400 hover:bg-violet-500/10" onClick={() => setMasaBerlaku(true)} data-testid={`${testid}-cek-masa-berlaku-btn`}><CalendarClock className="w-4 h-4 mr-1" /> Cek Masa Berlaku</Button>}
              {isAdmin && <Button size="sm" variant="outline" className="border-sky-500/40 text-sky-400 hover:bg-sky-500/10" onClick={() => setHsUsers(true)} data-testid={`${testid}-hotspot-users-btn`}><Wifi className="w-4 h-4 mr-1" /> Hotspot Users</Button>}
              {isAdmin && <Button size="sm" variant="outline" className="border-teal-500/40 text-teal-400 hover:bg-teal-500/10" onClick={() => setPppData(true)} data-testid={`${testid}-ppp-data-btn`}><Router className="w-4 h-4 mr-1" /> Data WiFi Rumahan</Button>}
            </>
          )}
          {!assignment?.assigned && (
            <>
              <span className="text-xs text-muted-foreground flex-1">Belum ada penanggung jawab — ambil tugas agar tidak double balasan.</span>
              <Button size="sm" onClick={claim} data-testid={`${testid}-claim-btn`}><UserCheck className="w-4 h-4 mr-1" /> Ambil Tugas</Button>
            </>
          )}
          {assignment?.assigned && assignment.assigned_to === me?.id && (
            <>
              <span className="text-xs font-medium text-emerald-500 flex-1" data-testid={`${testid}-assign-mine`}>Anda menangani chat ini ({assignment.assigned_name})</span>
              <Select value={delegateTo} onValueChange={setDelegateTo}>
                <SelectTrigger className="w-36 h-8 text-xs" data-testid={`${testid}-delegate-select`}><SelectValue placeholder="Delegasi ke..." /></SelectTrigger>
                <SelectContent>
                  {admins.filter((a) => a.id !== me?.id).map((a) => <SelectItem key={a.id} value={a.id} data-testid={`${testid}-delegate-opt-${a.name}`}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" disabled={!delegateTo} onClick={delegate} data-testid={`${testid}-delegate-btn`}><UserPlus className="w-4 h-4 mr-1" /> Delegasikan</Button>
              <Button size="sm" variant="ghost" onClick={release} data-testid={`${testid}-release-btn`}><UserX className="w-4 h-4 mr-1" /> Lepaskan</Button>
            </>
          )}
          {assignedOther && (
            <span className="text-xs font-medium text-amber-500 flex-1" data-testid={`${testid}-assign-other`}>Ditangani oleh {assignment.assigned_name} — Anda tidak bisa membalas agar tidak double balasan.</span>
          )}
          {superOverride && (
            <span className="text-xs font-medium text-sky-400 flex-1" data-testid={`${testid}-assign-super`}>Ditangani oleh {assignment.assigned_name} — sebagai Super Admin Anda tetap bisa membalas.</span>
          )}
        </div>
      )}
      {(assignment?.peer_name || assignment?.assigned_name || assignment?.last_admin) && (
        <div className="px-1 space-y-1.5">
          <button type="button" onClick={() => setShowHandlerHistory((v) => !v)} title="Klik untuk melihat riwayat penanganan" className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors" data-testid={`${testid}-handler-title`}>
            <UserCheck className="w-3.5 h-3.5 text-[#F04E23]" />
            <span>Chat — {assignment?.peer_name || "Agen"}{(assignment?.assigned ? assignment.assigned_name : assignment?.last_admin) ? ` — ${assignment.assigned ? assignment.assigned_name : assignment.last_admin}` : ""}</span>
            {assignment?.assigned && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-mono">diklaim</span>}
            {(assignment?.history || []).length > 0 && <span className="text-[9px] text-sky-400 font-mono">{showHandlerHistory ? "▲" : "▼"} riwayat</span>}
          </button>
          {isAdmin && assignment?.peer_agent && assignment?.peer_user && (
            <div className="flex items-center gap-2 flex-wrap text-[11px]" data-testid={`${testid}-peer-mutasi`}>
              <span className="font-mono text-muted-foreground">{assignment.peer_agent.code}</span>
              <span className={`font-mono font-semibold ${assignment.peer_user.balance < 0 ? "text-rose-400" : "text-emerald-400"}`} data-testid={`${testid}-peer-balance`}>Saldo {assignment.peer_user.balance < 0 ? "−" : ""}{rupiah(Math.abs(assignment.peer_user.balance))}</span>
              <AgentMutasiActions agent={assignment.peer_agent} user={assignment.peer_user} testid={`${testid}-peer`} />
            </div>
          )}
          {showHandlerHistory && (assignment?.history || []).length > 0 && (
            <div className="rounded-lg border border-border bg-accent/40 px-3 py-2 space-y-1" data-testid={`${testid}-handler-history`}>
              {assignment.history.map((h, i) => (
                <div key={i} className="flex items-center justify-between gap-2 text-[11px]" data-testid={`${testid}-history-${i}`}>
                  <span className="font-medium">{h.name}</span>
                  <span className="text-muted-foreground font-mono">{h.count}x balasan • {new Date(h.first).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}{h.last !== h.first ? ` — ${new Date(h.last).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}` : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {(typingUsers.length > 0 || lastRead) && (
        <div className="flex items-center justify-between gap-2 text-[10px] font-mono text-muted-foreground px-1" data-testid={`${testid}-presence`}>
          <span className="text-sky-400 animate-pulse" data-testid={`${testid}-typing`}>{typingUsers.length ? `${typingUsers.join(", ")} sedang mengetik...` : ""}</span>
          {lastRead && <span data-testid={`${testid}-last-read`}>Terakhir dibaca pukul {new Date(lastRead).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}</span>}
        </div>
      )}
      <div className={`${compact ? "flex-1 min-h-0" : "h-[55vh] min-h-72"} overflow-y-auto rounded-xl border border-border bg-accent/30 p-3 space-y-2`} data-testid={`${testid}-messages`}>
        {!messages.length && <div className="text-center text-xs text-muted-foreground py-10">Belum ada pesan — mulai percakapan.</div>}
        {messages.map((m) => {
          const mine = m.user_id === me?.id;
          // Susunan WhatsApp: sisi saya (admin↔agen) di kanan, lawan bicara di kiri
          const viewerIsAdmin = ["admin", "admin_fasnet"].includes(me?.role);
          const rightSide = isGroup ? mine : Boolean(m.sender_is_admin) === viewerIsAdmin;
          const canRetract = mine || ["admin", "admin_fasnet"].includes(me?.role);
          return (
            <div key={m.id} className={`group flex flex-col ${rightSide ? "items-end" : "items-start"}`} data-testid={`${testid}-msg-${m.id}`}>
              <div className={m.sticker ? "max-w-[80%]" : `max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-blue-600 text-white rounded-br-sm" : rightSide ? "bg-slate-700/80 text-white rounded-br-sm" : "bg-card text-card-foreground border border-border rounded-bl-sm"}`}>
                <div className={`text-[10px] font-semibold mb-0.5 ${mine ? "text-blue-200/90 text-right" : "text-sky-500"}`} data-testid={`${testid}-sender-${m.id}`}>{m.name}</div>
                {m.reply && (
                  <div className={`mb-1 rounded-lg border-l-2 px-2 py-1 text-[11px] ${mine ? "border-sky-300 bg-white/10" : "border-[#F04E23] bg-accent/60"}`} data-testid={`${testid}-quote-${m.id}`}>
                    <div className={`font-semibold text-[10px] ${mine ? "text-sky-200" : "text-[#F04E23]"}`}>{m.reply.name}</div>
                    <div className={`truncate ${mine ? "text-blue-100/80" : "text-muted-foreground"}`}>{m.reply.text}</div>
                  </div>
                )}
                {m.sticker ? (
                  <img src={`/stickers/${m.sticker}.webp`} alt="stiker FIBI" loading="lazy" className="w-28 h-28 sm:w-32 sm:h-32 object-contain" data-testid={`${testid}-sticker-${m.id}`} />
                ) : m.voice_path ? (
                  <div className="min-w-44" data-testid={`${testid}-voice-${m.id}`}>
                    <audio controls preload="metadata" src={`${API_BASE}/chat/files/${m.voice_path}`} className="h-9 w-full max-w-60" />
                    <div className={`text-[9px] mt-0.5 ${mine ? "text-blue-200" : "text-muted-foreground"}`}>Pesan suara • terhapus otomatis 2 jam setelah terkirim</div>
                  </div>
                ) : m.file_path ? (
                  <a href={`${API_BASE}/chat/files/${m.file_path}`} target="_blank" rel="noreferrer" className={`flex items-center gap-2 rounded-lg px-2.5 py-2 border ${mine ? "border-sky-300/40 bg-white/10 hover:bg-white/20" : "border-border bg-background/60 hover:bg-accent"} transition-colors min-w-44`} data-testid={`${testid}-pdf-${m.id}`}>
                    <FileText className="w-6 h-6 text-rose-400 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-xs font-semibold truncate max-w-48">{m.file_name || "dokumen.pdf"}</div>
                      <div className={`text-[9px] ${mine ? "text-blue-200" : "text-muted-foreground"}`}>PDF • {((m.file_size || 0) / 1024).toFixed(0)} KB • terhapus otomatis 30 menit</div>
                    </div>
                  </a>
                ) : m.location ? (
                  <a href={`https://www.google.com/maps?q=${m.location.lat},${m.location.lng}`} target="_blank" rel="noreferrer" className="block min-w-48" data-testid={`${testid}-loc-${m.id}`}>
                    <img src={`https://staticmap.openstreetmap.de/staticmap.php?center=${m.location.lat},${m.location.lng}&zoom=15&size=320x160&markers=${m.location.lat},${m.location.lng},red-pushpin`} alt="peta lokasi" loading="lazy" className="rounded-lg w-full max-w-64 h-32 object-cover bg-muted" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    <div className="flex items-center gap-1.5 mt-1 text-xs font-medium"><MapPin className="w-3.5 h-3.5 text-rose-400" /> {m.text || "Lokasi"} <span className="underline underline-offset-2 opacity-80">Buka di Maps</span></div>
                    <div className={`text-[10px] font-mono ${mine ? "text-blue-200" : "text-muted-foreground"}`}>{m.location.lat}, {m.location.lng}</div>
                  </a>
                ) : m.image_path ? (
                  <ZoomImg src={`${API_BASE}/chat/files/${m.image_path}`} alt="lampiran gambar" className="rounded-lg max-h-48 hover:opacity-90 transition-opacity" data-testid={`${testid}-img-${m.id}`} />
                ) : (
                  <div className="whitespace-pre-wrap break-words">{renderText(m.text, mine)}</div>
                )}
                {m.link && (
                  <a href={m.link} className={`mt-1 inline-flex items-center gap-1 text-[11px] font-semibold underline underline-offset-2 ${mine ? "text-sky-200 hover:text-white" : "text-[#F04E23] hover:text-[#ff6a3d]"} transition-colors`} data-testid={`${testid}-link-${m.id}`}>{m.link_label || "Buka di Lacak Aset"} →</a>
                )}
                {pppDiagOf(m) && (
                  <button type="button" onClick={() => setDiag(pppDiagOf(m))} className={`mt-1.5 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${mine ? "bg-white/20 hover:bg-white/30 text-white" : "bg-[#F04E23] hover:bg-[#ff6a3d] text-white"}`} data-testid={`${testid}-diag-${m.id}`}><Zap className="w-3 h-3" /> Diagnosa Secret</button>
                )}
                {m.portal_order_id && <PortalConfirmInline orderId={m.portal_order_id} done={m.portal_done} isAdmin={["admin", "admin_fasnet"].includes(me?.role)} />}
                <div className={`text-[9px] mt-0.5 flex items-center gap-1 ${mine && !m.sticker ? "text-blue-200 justify-end" : "text-muted-foreground"}`}>
                  {fmtDateTime(m.created_at)}
                  {mine && (
                    m.read ? (
                      <CheckCheck className="w-3 h-3 text-sky-300" data-testid={`${testid}-read-${m.id}`} title={m.read_at ? `Dibaca pukul ${new Date(m.read_at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}` : "Dibaca"} />
                    ) : (
                      <Check className="w-3 h-3 text-blue-200/50" data-testid={`${testid}-read-${m.id}`} title="Terkirim — belum dibaca" />
                    )
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-0.5 px-1">
                <button type="button" onClick={() => setReplyTarget(m)} className="text-[9px] text-muted-foreground hover:text-sky-400 transition-colors inline-flex items-center gap-0.5 opacity-60 md:opacity-0 md:group-hover:opacity-100" data-testid={`${testid}-reply-${m.id}`}><Reply className="w-2.5 h-2.5" /> Balas</button>
                {canRetract && (
                  <button type="button" onClick={() => retract(m.id)} className="text-[9px] text-muted-foreground hover:text-rose-500 transition-colors" data-testid={`${testid}-retract-${m.id}`}>Tarik</button>
                )}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <PppDiagDialog routerId={diag?.router_id} name={diag?.secret_name} open={!!diag} onClose={() => setDiag(null)} isAdmin={isAdmin} />
      {replyTarget && (
        <div className="flex items-center gap-2 rounded-lg border-l-2 border-[#F04E23] bg-accent/60 px-3 py-2" data-testid={`${testid}-reply-preview`}>
          <Reply className="w-3.5 h-3.5 text-[#F04E23] shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-semibold text-[#F04E23]">Membalas {replyTarget.name}</div>
            <div className="text-[11px] text-muted-foreground truncate">{replyTarget.text || (replyTarget.sticker ? "[Stiker]" : "[Gambar]")}</div>
          </div>
          <button type="button" onClick={() => setReplyTarget(null)} className="text-muted-foreground hover:text-foreground transition-colors" data-testid={`${testid}-reply-cancel`}><X className="w-3.5 h-3.5" /></button>
        </div>
      )}
      {templates && templates.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-templates`}>
          {templates.map((t, i) => (
            <button key={i} type="button" title={t.text} onClick={() => setText(t.text)}
              className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20 transition-colors"
              data-testid={`${testid}-template-${i}`}>
              {t.label}
            </button>
          ))}
        </div>
      )}
      {isAdmin && !assignedOther && quickReplies.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid={`${testid}-quick-replies`}>
          <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Zap className="w-3 h-3 text-amber-400" /> Balasan cepat:</span>
          {quickReplies.map((q, i) => (
            <button key={i} type="button" onClick={() => setText(renderQuickReply(q.text))} title="Isi ke kolom pesan — bisa diedit sebelum dikirim"
              className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-amber-500/10 text-amber-400 border border-amber-500/30 hover:bg-amber-500/20 transition-colors"
              data-testid={`${testid}-quick-reply-${i}`}>
              {q.label}
            </button>
          ))}
        </div>
      )}
      {isAdmin && showStickers && (
        <div className="grid grid-cols-3 gap-2 rounded-xl border border-border bg-card p-2" data-testid={`${testid}-sticker-picker`}>
          {FIBI_STICKERS.map((s) => (
            <button key={s.id} type="button" title={s.label} onClick={() => sendSticker(s.id)} disabled={assignedOther}
              className="rounded-lg p-1 hover:bg-accent transition-colors disabled:opacity-40" data-testid={`${testid}-sticker-${s.id}`}>
              <img src={`/stickers/${s.id}.webp`} alt={s.label} loading="lazy" className="w-full aspect-square object-contain" />
            </button>
          ))}
        </div>
      )}
      <form onSubmit={send} className="flex gap-1.5 sm:gap-2 items-end relative">
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={sendImage} data-testid={`${testid}-file`} />
        <input ref={pdfRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={sendPdf} data-testid={`${testid}-pdf-file`} />
        {attachOpen && (
          <div className="absolute bottom-full mb-2 left-0 z-30 grid grid-cols-4 gap-1 rounded-2xl border border-border bg-popover p-2 shadow-xl" data-testid={`${testid}-attach-menu`}>
            {[
              isAdmin && ["stickers", "Stiker", Smile, "bg-amber-500", () => setShowStickers((v) => !v)],
              ["attach", "Foto", ImagePlus, "bg-violet-600", () => fileRef.current?.click()],
              ["attach-pdf", "File PDF", FileText, "bg-sky-600", () => pdfRef.current?.click()],
              ["share-loc", "Tikor", MapPin, "bg-rose-500", sendLocation],
            ].filter(Boolean).map(([id, label, Icon, color, fn]) => (
              <button key={id} type="button" disabled={uploading || assignedOther} onClick={() => { setAttachOpen(false); fn(); }} className="flex flex-col items-center gap-1 w-16 py-1.5 rounded-xl hover:bg-accent disabled:opacity-40" data-testid={`${testid}-${id}`}>
                <span className={`w-11 h-11 rounded-full ${color} text-white flex items-center justify-center shadow`}><Icon className="w-5 h-5" /></span>
                <span className="text-[10px] font-medium">{label}</span>
              </button>
            ))}
          </div>
        )}
        <Button type="button" size="icon" variant="outline" title="Lampirkan: stiker, foto, file, tikor" disabled={uploading || assignedOther} onClick={() => setAttachOpen((v) => !v)} className="shrink-0 rounded-full" data-testid={`${testid}-plus`}>
          <Plus className={`w-5 h-5 transition-transform ${attachOpen ? "rotate-45" : ""}`} />
        </Button>
        <Button type="button" size="icon" variant={recording ? "destructive" : "outline"} title={recording ? "Berhenti & kirim pesan suara" : "Rekam pesan suara (maks 60 detik, terhapus otomatis 2 jam)"} disabled={uploading || assignedOther} onClick={() => (recording ? stopRecording(true) : startRecording())} className="shrink-0 rounded-full" data-testid={`${testid}-mic`}>
          {recording ? <Square className="w-4 h-4 animate-pulse" /> : <Mic className="w-4 h-4" />}
        </Button>
        {recording ? (
          <div className="flex-1 h-10 px-3 rounded-md border border-rose-500/50 bg-rose-500/10 flex items-center gap-2 text-rose-400 text-xs font-mono" data-testid={`${testid}-recording`}>
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" /> Merekam 0:{String(recSecs).padStart(2, "0")} / 1:00
          </div>
        ) : (
          <div className="flex-1 relative">
            {mentionOpts.length > 0 && (
              <div className="absolute bottom-full mb-1 left-0 z-20 w-64 rounded-lg border border-border bg-popover shadow-lg overflow-hidden" data-testid={`${testid}-mention-list`}>
                <div className="px-2 py-1 text-[10px] text-muted-foreground border-b border-border">Tag admin — notifikasi suara & chat terbuka otomatis</div>
                {mentionOpts.map((a) => (
                  <button key={a.id} type="button" onMouseDown={(e) => { e.preventDefault(); pickMention(a); }} className="w-full text-left px-2.5 py-1.5 text-xs hover:bg-accent flex items-center gap-2" data-testid={`${testid}-mention-${a.id}`}>
                    <AtSign className="w-3 h-3 text-amber-400" /> <span className="font-semibold">{a.name}</span> <span className="text-muted-foreground truncate">{a.email}</span>
                  </button>
                ))}
              </div>
            )}
            {text.includes("\n") ? (
              <Textarea value={text} rows={Math.min(8, text.split("\n").length + 1)} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); } }} placeholder="Tulis pesan..." disabled={uploading || assignedOther} className={`text-sm ${inputClass}`} data-testid={`${testid}-input`} />
            ) : (
            <Input value={text} onChange={(e) => { setText(e.target.value); const now = Date.now(); if (now - typingSentRef.current > 2000) { typingSentRef.current = now; api.post("/chat/typing", { channel }).catch(() => {}); } }} onKeyDown={(e) => { if (e.key === "Tab" && mentionOpts.length) { e.preventDefault(); pickMention(mentionOpts[0]); } }} onPaste={onPaste} placeholder={assignedOther ? `Ditangani ${assignment?.assigned_name}` : uploading ? "Mengirim lampiran..." : "Tulis pesan..."} disabled={uploading || assignedOther} className={inputClass} data-testid={`${testid}-input`} />
            )}
          </div>
        )}
        <Button type="submit" disabled={sending || uploading || !text.trim() || assignedOther} className="shrink-0 rounded-full" data-testid={`${testid}-send`}>
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  );
}
